import {Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ShopService} from "../../service/shop.service";
import {Product} from "../../../../models/product";
import {Observable, switchMap, take} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {CartService} from "../../../cart/service/cart.service";
import {Carousel, initTE, Sidenav, Toast} from "tw-elements";
import {OrderItem} from "../../../../models/order-item";
import {Link} from "../../../../models/link";
import {environment} from "../../../../environments/environment.production";
import {Meta, Title} from '@angular/platform-browser';
import {DOCUMENT} from "@angular/common";
import {extractFaq, setCanonicalUrl, setSocialMeta, setStructuredData} from "../../../../services/util/util";
import {DELIVERY_DAYS_MAX, DELIVERY_DAYS_MIN, DELIVERY_EXPENSES, DELIVERY_TIME_TEXT, FREE_DELIVERY_THRESHOLD} from "../../../../services/util/constants";
import {FBQ} from "../../../../models/fbq";
import {buildProductFaq, FaqEntry, mergeFaq} from "./product-faq";
import {AnalyticsService} from "../../../../services/util/analytics.service";
import {ReviewService} from "../../../../services/review/review.service";
import {formatRating, reviewCountLabel, ReviewSummary} from "../../../../models/review";

declare let fbq: FBQ;

@Component({
    selector: 'app-product-page',
    templateUrl: './product-page.component.html',
    styleUrl: './product-page.component.css'
})
export class ProductPageComponent implements OnInit, OnDestroy {

    product!: Observable<Product>;

    products: Product[] = [];

    /** Phones: a buy bar at the bottom of the screen while the main buttons are out of view. */
    showStickyBuy = false;

    private buyButtonsObserver?: IntersectionObserver;

    @ViewChild('mainBuyButtons')
    set mainBuyButtons(element: ElementRef<HTMLElement> | undefined) {
        this.buyButtonsObserver?.disconnect();
        if (!element || typeof IntersectionObserver === 'undefined') {
            return;
        }
        this.buyButtonsObserver = new IntersectionObserver(([entry]) =>
            this.zone.run(() => this.showStickyBuy = !entry.isIntersecting));
        this.buyButtonsObserver.observe(element.nativeElement);
    }

    /** Approved reviews of this product - stars under the title, list at the bottom. */
    reviews: ReviewSummary | null = null;

    /** Generated questions not already answered in the admin content - shown below it. */
    generatedFaq: FaqEntry[] = [];

    orderItemControl: UntypedFormGroup = new UntypedFormGroup({
        quantity: new UntypedFormControl('', [Validators.required]),
    });

    toastMessage: string = '';

    links: Link[] = [
        {
            text: "Почетна",
            route: environment.navigation.home,
        },
        {
            text: "Продавница",
            route: environment.navigation.shop,
        }]

    constructor(private route: ActivatedRoute,
                private readonly router: Router,
                private shopService: ShopService,
                private cartService: CartService,
                private meta: Meta,
                private titleService: Title,
                private analytics: AnalyticsService,
                private zone: NgZone,
                private reviewService: ReviewService,
                @Inject(DOCUMENT) private document: Document) {
    }

    ngOnDestroy(): void {
        this.buyButtonsObserver?.disconnect();
    }

    protected readonly formatRating = formatRating;
    protected readonly reviewCountLabel = reviewCountLabel;

    scrollToReviews(): void {
        this.document.getElementById('reviews')?.scrollIntoView({behavior: 'smooth'});
    }

    /** What Macedonian buyers check before ordering, right under the price. */
    trustPoints(product: Product): string[] {
        return [
            'Оригинален Forever Living производ',
            'Плаќање при достава, во готово',
            `Достава низ цела Македонија за ${DELIVERY_TIME_TEXT}`,
            product.price >= FREE_DELIVERY_THRESHOLD
                ? 'Бесплатна достава за овој производ'
                : `Бесплатна достава за нарачки над ${FREE_DELIVERY_THRESHOLD} ден.`
        ];
    }

    ngOnInit(): void {
        this.product = this.route.paramMap.pipe(
            switchMap(params => {
                const slugAndId = params.get('slugAndId')!;

                let id: number;

                if (slugAndId.includes('-')) {
                    id = +slugAndId.split('-').pop()!;
                } else {
                    id = +slugAndId;
                }
                return this.shopService.getProductById(id);
            })
        );

        this.product.subscribe(product => {
            this.updateLinks(product.category);
            const canonicalUrl = environment.domain.frontend + environment.navigation.shop + '/product/' + (product?.slug ? product?.slug + '-' + product?.id : product?.id);
            setCanonicalUrl(canonicalUrl);
            this.setTitle(product);
            this.setMetaDescription(product);
            this.reviews = null;
            this.setStructuredData(product, canonicalUrl);
            this.reviewService.forProduct(product.id!).subscribe({
                next: reviews => {
                    this.reviews = reviews;
                    if (reviews.count > 0) {
                        this.setStructuredData(product, canonicalUrl);
                    }
                },
                error: () => this.reviews = null
            });
            this.setMetaKeywords(product);
            setSocialMeta(this.meta, {
                title: this.titleService.getTitle(),
                description: this.metaDescription(product),
                url: canonicalUrl,
                image: this.absoluteImageUrl(product.images?.[0] ?? ''),
                type: 'product'
            });
            this.shopService.getProductsByIds(product.productAdIds).subscribe((products) => this.products = products);
            this.analytics.viewItem(product);
            fbq('track', 'ViewContent', {
                content_name: product?.name,
                content_ids: [product.id],
                content_category: product?.category,
                value: product?.price,
                currency: 'MKD',
                content_type: 'product'
            });
            setTimeout(() => initTE({Carousel}), 500);
        });
    }

    /** The stored image paths are already absolute URLs - only relative ones need the domain. */
    private absoluteImageUrl(image: string): string {
        if (!image || image.startsWith('http')) {
            return image;
        }
        return environment.domain.frontend + (image.startsWith('/') ? '' : '/') + image;
    }

    setStructuredData(product: Product, canonicalUrl: string) {
        setStructuredData('product-structured-data', {
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": product.name,
            "image": product.images?.map(image => this.absoluteImageUrl(image)) ?? [],
            "description": product.description ?? '',
            "sku": product.designID ?? '',
            // Only real, approved reviews from this site - Google shows the stars in results.
            ...(this.reviews && this.reviews.count > 0 ? {
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": this.reviews.average,
                    "reviewCount": this.reviews.count,
                    "bestRating": 5,
                    "worstRating": 1
                },
                "review": this.reviews.reviews.slice(0, 10).map(review => ({
                    "@type": "Review",
                    "author": {"@type": "Person", "name": review.authorName},
                    "datePublished": review.createdAt.substring(0, 10),
                    "reviewBody": review.text,
                    "reviewRating": {"@type": "Rating", "ratingValue": review.rating, "bestRating": 5, "worstRating": 1}
                }))
            } : {}),
            "brand": {
                "@type": "Brand",
                "name": "Forever Living"
            },
            "offers": {
                "@type": "Offer",
                "priceCurrency": "MKD",
                "price": product.price ?? '',
                "itemCondition": "https://schema.org/NewCondition",
                "availability": "https://schema.org/InStock",
                "url": canonicalUrl,
                // Google warns without these; the price is re-read on every crawl anyway.
                "validFrom": (product.created || new Date().toISOString()).substring(0, 10),
                "priceValidUntil": `${new Date().getFullYear() + 1}-12-31`,
                "seller": {
                    "@type": "Organization",
                    "name": "Алое вера гел"
                },
                // What the delivery to North Macedonia costs for this product on its own.
                "shippingDetails": {
                    "@type": "OfferShippingDetails",
                    "shippingDestination": {"@type": "DefinedRegion", "addressCountry": "MK"},
                    "shippingRate": {
                        "@type": "MonetaryAmount",
                        "value": (product.price ?? 0) >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_EXPENSES,
                        "currency": "MKD"
                    },
                    // Packed the same or next working day, then with the courier - 2-7 working days in all, as the order email says.
                    "deliveryTime": {
                        "@type": "ShippingDeliveryTime",
                        "handlingTime": {"@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "DAY"},
                        "transitTime": {"@type": "QuantitativeValue", "minValue": DELIVERY_DAYS_MIN, "maxValue": DELIVERY_DAYS_MAX - 1, "unitCode": "DAY"}
                    }
                }
            }
        });

        // Breadcrumbs as Google shows them under the result: Почетна › Продавница › category.
        setStructuredData('breadcrumb-structured-data', {
            "@context": "https://schema.org/",
            "@type": "BreadcrumbList",
            "itemListElement": [
                ...this.links.map((link, index) => ({
                    "@type": "ListItem",
                    "position": index + 1,
                    "name": link.text,
                    "item": environment.domain.frontend + link.route
                })),
                {"@type": "ListItem", "position": this.links.length + 1, "name": product.name}
            ]
        });

        const contentFaq = extractFaq(product.content);
        const faq = mergeFaq(contentFaq, buildProductFaq(product));
        this.generatedFaq = faq.slice(contentFaq.length);
        if (faq.length > 0) {
            setStructuredData('faq-structured-data', {
                "@context": "https://schema.org/",
                "@type": "FAQPage",
                "mainEntity": faq.map(entry => ({
                    "@type": "Question",
                    "name": entry.question,
                    "acceptedAnswer": {"@type": "Answer", "text": entry.answer}
                }))
            });
        } else {
            this.document.getElementById('faq-structured-data')?.remove();
        }
    }

    get quantityControl(): UntypedFormControl {
        return this.orderItemControl.get('quantity') as UntypedFormControl;
    }

    addToCart() {
        this.createOrderItem().then(() => this.openCartOverview()).catch(error => {
            this.openToast('Failed to add item to cart');
            console.error(error);
        });
    }

    openCartOverview(): void {
        new Sidenav(document.getElementById("cart-overview")).show();
    }

    buyNow() {
        this.createOrderItem().then(() => {
            this.router.navigateByUrl('/checkout');
        }).catch(error => {
            this.openToast('Failed to proceed to checkout');
            console.error(error);
        });
    }

    private createOrderItem(): Promise<OrderItem> {
        return new Promise((resolve, reject) => {
            this.product.pipe(take(1)).subscribe({
                next: (product) => {
                    const orderItem: OrderItem = {
                        product: product,
                        quantity: this.quantityControl.value
                    };

                    this.cartService.updateCartItems([...this.cartService.retrieveItemsFromLocalStorage(), orderItem]);
                    this.updateLocalStorage(orderItem);
                    resolve(orderItem);
                    this.analytics.addToCart(product, this.quantityControl.value);
                    fbq('track', 'AddToCart', {
                        content_name: product?.name,
                        contents: {
                            id: product.id,
                            quantity: this.quantityControl.value,
                        },
                        content_category: product?.category,
                        value: product?.price,
                        currency: 'MKD',
                        content_type: 'product'
                    });
                },
                error: (error) => reject(error)
            });
        });
    }

    private updateLocalStorage(orderItem: OrderItem): void {
        const existingItems = this.getExistingItems();
        existingItems.push(orderItem);
        localStorage.setItem('cartItems', JSON.stringify(existingItems));
    }

    private getExistingItems(): OrderItem[] {
        const existingItemsJson = localStorage.getItem('cartItems');
        return existingItemsJson ? JSON.parse(existingItemsJson) : [];
    }

    setTitle(product: Product) {
        // The price in the title earns clicks on buying searches ("aloe vera gel cena").
        // Many product names already start with "Алое вера гел", which the old suffix repeated.
        this.titleService.setTitle(`${product.name} – ${product.price} ден. | aloeveragel.mk`);
    }

    updateLinks(category: string) {
        if (category) {
            const newLink: Link = {
                text: this.setProductCategory(category),
                route: `${environment.navigation.shop}/${category.replaceAll("_", "-").toLowerCase()}`
            };

            const linkExists = this.links.some(link => link.route === newLink.route);

            if (!linkExists) {
                this.links.push(newLink);
            }
        }
    }

    setMetaDescription(product: Product) {
        this.meta.updateTag({name: 'description', content: this.metaDescription(product)});
    }

    /** Delivery and cash on delivery are what a Macedonian buyer checks first - say it in the result. */
    private metaDescription(product: Product): string {
        const base = (product.metaDescription ?? '').trim();
        const delivery = 'Достава низ цела Македонија, плаќање при достава.';
        return base.length + delivery.length + 1 <= 160 ? `${base} ${delivery}`.trim() : base;
    }

    private setMetaKeywords(product: Product): void {
        const keywords = product.keywords?.join(', ') ?? '';

        this.meta.updateTag({name: 'keywords', content: keywords});
    }

    openToast(message: string) {
        const toast = new Toast(document.getElementById("toast"));
        this.toastMessage = message;
        toast.show();
    }

    setProductCategory(category: string | undefined) {
        switch (category) {
            case 'ALOE_VERA_PIJALOCI':
                return "Алое Вера Пијалоци"
            case 'PCELNI_PROIZVODI':
                return "Пчелни Производи"
            case 'DODATOCI_VO_ISHRANA':
                return "Додатоци во исхрана"
            case 'LICNA_NEGA':
                return "Лична нега"
            case 'NEGA_ZA_LICE':
                return "Нега за лице"
            case 'ETERICNI_MASLA':
                return "Етерични масла"
            case 'NEGA_ZA_TELO':
                return "Нега за тело"
            case 'KONTROLA_NA_TELESNA_TEZINA':
                return "Контрола на телесната тежина"
            default :
                return ""
        }
    }

}